import {
  isDesignIn,
  isRoll,
  isCustomShapeSticker,
  flattened,
  isMobileMode,
  isStickerFlow,
  isSheetFlow,
  isRollFlow,
  getHelpSections,
} from '@app/utils/utils';
import {
  HELP_SECTION_PRODUCT_TYPE,
  HELP_SECTION_ART_SIZE_POSITION,
  HELP_SECTION_REQUEST_CODES,
  HELP_SECTION_UPLOAD_ART,
  HELP_SECTION_WHITE_INK,
  HELP_TOPIC_WHITE_INK,
  HELP_TOPIC_UPLOAD_ART,
  HELP_TOPIC_ART_SIZE_POSITION,
  HELP_TOPIC_SHAPE_AND_SIZE,
  HELP_TOPIC_POSITION_AND_ROTATION,
  HELP_TOPIC_CONTOUR_AND_BACKGROUND,
  HELP_TOPIC_REQUEST_CODES,
  HELP_TOPIC_TRANSPARENCY_CODES,
  HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
  HELP_TOPIC_CONTACT_US,
  HELP_TOPIC_ROLL_LABELS,
  HELP_TOPIC_DIE_CUT_STICKERS,
  HELP_TOPIC_STICKER_DESIGN,
  HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
  HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED,
  HELP_TOPIC_ART_SIZE_POSITION_FLATTENED,
  HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED,
  HELP_TOPIC_WHITE_INK_OPTIONS,
  HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD,
  HELP_TOPIC_SHEET_LABELS,
  HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
  HELP_TOPIC_SHEET_SIZE,
  HELP_TOPIC_SHAPE_CORNERS_SIZE,
  WORKING_AREA_WARNING_STRETCHED_KEY,
  WORKING_AREA_WARNING_FLATTENED_KEY,
  WORKING_AREA_WARNING_OPTIMIZED_KEY,
  WORKING_AREA_WARNING_ADJUSTED_KEY,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_KEY,
  WORKING_AREA_WARNING_HPI_WHITE_KEY,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  WORKING_AREA_WARNING_OPTIMIZED_CODES,
  WORKING_AREA_WARNING_STRETCHED_CODE,
  WORKING_AREA_WARNING_FLATTENED_CODE,
  WORKING_AREA_WARNING_ADJUSTED_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE,
  WORKING_AREA_WARNING_HPI_WHITE_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE,
  HELP_TOPIC_UPLOAD_ART_PREPARE_FILES,
  HELP_TOPIC_PRODUCT_TYPE,
  HELP_TOPIC_UPLOAD_ART_DESIGN_IN,
  HELP_TOPIC_UPLOAD_CODES,
  HELP_SECTION_UPLOAD_CODES,
  HELP_SECTION_SAFETY_TRIM_AND_BLEED,
} from '@app/constants';

const productSectionTypeTitleKey = flowConfig => {
  if (isDesignIn(flowConfig)) {
    return HELP_TOPIC_TRANSPARENCY_CODES;
  } else if (isRoll(flowConfig)) {
    return HELP_TOPIC_ROLL_LABELS;
  } else if (isSheetFlow(flowConfig)) {
    return HELP_TOPIC_SHEET_LABELS;
  } else {
    return HELP_TOPIC_DIE_CUT_STICKERS;
  }
};

const artSizeAndPositionTitleKey = flowConfig => {
  if (isStickerFlow(flowConfig)) {
    return HELP_TOPIC_STICKER_DESIGN;
  } else if (isSheetFlow(flowConfig)) {
    return HELP_TOPIC_SHEET_SIZE;
  } else {
    return HELP_TOPIC_ART_SIZE_POSITION;
  }
};

export const getAvailableHelpSections = ({flowConfig,matrix}) => {
  const availableSections = getHelpSections(flowConfig);
  return [
    {
      name: HELP_SECTION_PRODUCT_TYPE,
      titleKey: productSectionTypeTitleKey(flowConfig),
      subsections: !isDesignIn(flowConfig) ? [HELP_TOPIC_CONTACT_US] : [],
      available: availableSections.includes('PRODUCT_TYPE'),
      order: 0,
    },
    {
      name: HELP_SECTION_UPLOAD_ART,
      titleKey: isDesignIn(flowConfig) ? HELP_TOPIC_UPLOAD_ART_DESIGN_IN : HELP_TOPIC_UPLOAD_ART,
      subsections: [
          ...isSheetFlow(flowConfig) ? [HELP_TOPIC_UPLOAD_ART_PREPARE_FILES, HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD ] : []
      ],
      available: availableSections.includes('UPLOAD_ART'),
      order: 1,
    },
    {
      name: HELP_SECTION_ART_SIZE_POSITION,
      titleKey: artSizeAndPositionTitleKey(flowConfig),
      subsections: [
        isStickerFlow(flowConfig) && HELP_TOPIC_SHAPE_AND_SIZE,
        isStickerFlow(flowConfig) &&
          (!isCustomShapeSticker(flowConfig) ? HELP_TOPIC_POSITION_AND_ROTATION : HELP_TOPIC_CONTOUR_AND_BACKGROUND),
        isSheetFlow(flowConfig) && HELP_TOPIC_SHAPE_CORNERS_SIZE,
        !isCustomShapeSticker(flowConfig) && !isDesignIn(flowConfig) && HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
      ],
      available: availableSections.includes('ART_SIZE_POSITION'),
      order: 2,
    },
    {
      name: HELP_SECTION_SAFETY_TRIM_AND_BLEED,
      titleKey: HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
      hasSubsections: false,
      available: availableSections.includes('SAFETY_TRIM_AND_BLEED'),
      order: 3,
    },
    {
      name: HELP_SECTION_REQUEST_CODES,
      titleKey: HELP_TOPIC_REQUEST_CODES,
      hasSubsections: false,
      available: availableSections.includes('REQUEST_CODES'),
      order: 4,
    },
    {
      name: HELP_SECTION_UPLOAD_CODES,
      titleKey: HELP_TOPIC_UPLOAD_CODES,
      hasSubsections: false,
      available: availableSections.includes('UPLOAD_CODES'),
      order: 5,
    },
    {
      name: HELP_SECTION_WHITE_INK,
      titleKey: HELP_TOPIC_WHITE_INK,
      subsections: [HELP_TOPIC_WHITE_INK_OPTIONS, isSheetFlow(flowConfig) && HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD],
      available: !isMobileMode(matrix.ctx) && availableSections.includes('WHITE_INK'),
      order: 6,
    },
  ].filter(({ available }) => !!available);
};

export const getAvailableHelpTopics = availableHelpSections => {
  const getTopicsForSectionName = sectionName => {
    const { titleKey: topicKey, subsections = [] } = availableHelpSections.find(({ name }) => name === sectionName) || {};
    return [
      { topicKey, sectionName },
      ...subsections.filter(Boolean).map(topicKey => ({ topicKey, sectionName, isSubsection: true })),
    ];
  };

  const availableHelpTopics = availableHelpSections
    .sort(({ order: prev }, { order: next }) => prev - next)
    .map(({ name: sectionName }) => sectionName)
    .map(getTopicsForSectionName);

  return flattened(availableHelpTopics);
};

const workingAreaWarningAdjusted = {
  key: WORKING_AREA_WARNING_ADJUSTED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_ADJUSTED,
};
const workingAreaWarningFlattened = {
  key: WORKING_AREA_WARNING_FLATTENED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_FLATTENED,
};
const workingAreaWarningStretched = {
  key: WORKING_AREA_WARNING_STRETCHED_KEY,
  helpSection: HELP_SECTION_ART_SIZE_POSITION,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
};
const sheetWorkingAreaWarningStretched = {
  key: WORKING_AREA_WARNING_STRETCHED_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_PREPARE_FILES,
};
const designInWorkingAreaWarningStretched = {
  key: WORKING_AREA_WARNING_STRETCHED_KEY,
  helpSection: HELP_SECTION_SAFETY_TRIM_AND_BLEED,
  helpTopic: HELP_TOPIC_ART_SIZE_POSITION_STRETCHED,
};
const workingAreaWarningCustomShape = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
};
const workingAreaWarningHPIWhite = {
  key: WORKING_AREA_WARNING_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_WHITE_INK,
  helpTopic: HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD,
};
const workingAreaWarningCustomShapeHPIWhite = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_UPLOAD_ART,
  helpTopic: HELP_TOPIC_UPLOAD_ART_LABEL_SHAPE_YOUR_UPLOAD,
};
const rollWorkingAreaWarningHPIWhite = {
  key: WORKING_AREA_WARNING_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_PRODUCT_TYPE,
  helpTopic: HELP_TOPIC_PRODUCT_TYPE,
};
const rollWorkingAreaWarningCustomShapeHPIWhite = {
  key: WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_KEY,
  helpSection: HELP_SECTION_PRODUCT_TYPE,
  helpTopic: HELP_TOPIC_PRODUCT_TYPE,
};
const workingAreaWarningOptimized = [
  ...WORKING_AREA_WARNING_OPTIMIZED_CODES.map(optimizedCode => ({
    [optimizedCode]: {
      key: WORKING_AREA_WARNING_OPTIMIZED_KEY,
      helpSection: HELP_SECTION_ART_SIZE_POSITION,
      helpTopic: HELP_TOPIC_ART_SIZE_POSITION_OPTIMIZED,
    },
  })),
];
export const getWorkingAreaWarningsList = flowConfig => {
  let stretchedWarning;
  if (isSheetFlow(flowConfig)) {
    stretchedWarning = sheetWorkingAreaWarningStretched;
  } else if (isDesignIn(flowConfig)) {
    stretchedWarning = designInWorkingAreaWarningStretched;
  } else {
    stretchedWarning = workingAreaWarningStretched;
  }

  return Object.assign(
    {
      [WORKING_AREA_WARNING_ADJUSTED_CODE]: workingAreaWarningAdjusted,
      [WORKING_AREA_WARNING_FLATTENED_CODE]: workingAreaWarningFlattened,
      [WORKING_AREA_WARNING_STRETCHED_CODE]: stretchedWarning,
      [WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE]: workingAreaWarningCustomShape,
      [WORKING_AREA_WARNING_HPI_WHITE_CODE]: isRollFlow(flowConfig)
        ? rollWorkingAreaWarningHPIWhite
        : workingAreaWarningHPIWhite,
      [WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE]: isRollFlow(flowConfig)
        ? rollWorkingAreaWarningCustomShapeHPIWhite
        : workingAreaWarningCustomShapeHPIWhite,
    },
    ...workingAreaWarningOptimized,
  );
};

export const setLabelTypePart = (labels, conditions) => {
  const index = conditions.findIndex((i) => i === true);
  return index !== -1 ? labels[index] : labels[labels.length - 1];
};
